import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FaceGuide } from "@/components/FaceGuide";
import { Camera, CheckCircle2, Loader2, UserCheck, AlertTriangle, ScanFace } from "lucide-react";
export default function Kiosk() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const pauseRef = useRef(0);
  const busyRef = useRef(false);
  const [school, setSchool] = useState(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {status, name, time, msg}
  const [reg, setReg] = useState("");

  const checkin = useCallback(async () => {
    if (!reg.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/kiosk/check-in", { kiosk_token: token, registration: reg.trim() });
      if (data.matched) {
        setResult({ status: data.already ? "already" : "ok", name: data.student_name, time: data.time,
          msg: data.already ? "Chegada já registrada hoje" : "Chegada registrada" });
        setReg("");
      } else {
        setResult({ status: "err", msg: "Matrícula não encontrada" });
      }
      setTimeout(() => setResult(null), 5000);
    } catch (e) {
      const d = e.response?.data?.detail;
      setResult({ status: "err", msg: typeof d === "string" ? d : "Falha no registro" });
      setTimeout(() => setResult(null), 4000);
    } finally { setBusy(false); }
  }, [reg, busy, token]);

  useEffect(() => {
    if (!token) { setError("Link de quiosque inválido (token ausente)."); return; }
    api.get("/kiosk/info", { params: { kiosk_token: token } })
      .then((r) => setSchool(r.data))
      .catch(() => setError("Quiosque inválido ou expirado. Solicite um novo link à gestão."));
  }, [token]);

  const stopCamera = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setRunning(false);
  }, []);

  const capture = useCallback(async () => {
    if (!videoRef.current || busyRef.current || document.hidden || Date.now() < pauseRef.current) return;
    const v = videoRef.current;
    if (!v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = 480; canvas.height = 360;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const image_base64 = canvas.toDataURL("image/jpeg", 0.82);
    busyRef.current = true; setBusy(true);
    try {
      const { data } = await api.post("/kiosk/identify", { kiosk_token: token, image_base64 });
      if (data.reason === "sem_biometria") {
        pauseRef.current = Date.now() + 60000;
        setResult({ status: "err", msg: "Nenhum aluno com biometria cadastrada nesta escola. Cadastre as biometrias para usar o quiosque." });
        setTimeout(() => setResult(null), 10000);
      }
      if (data.matched) {
        setResult({
          status: data.already ? "already" : "ok",
          name: data.student_name, time: data.time,
          msg: data.already ? "Chegada já registrada hoje" : "Chegada registrada",
        });
        setTimeout(() => setResult(null), 5000);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d) { setResult({ status: "err", msg: typeof d === "string" ? d : "Falha na identificação" }); setTimeout(() => setResult(null), 4000); }
    } finally { busyRef.current = false; setBusy(false); }
  }, [token]);

  const startCamera = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setRunning(true);
      loopRef.current = setInterval(capture, 3500);
    } catch {
      setError("Não foi possível acessar a câmera. Autorize o uso da câmera no navegador.");
    }
  }, [capture]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="kiosk-page">
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center px-4 py-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-blue-400">
          <ScanFace className="h-5 w-5" /> ACOMPANHA · Presença
        </div>
        <h1 className="mt-2 text-center font-display text-3xl font-black sm:text-4xl" data-testid="kiosk-school-name">
          {school?.school_name || (error ? "Quiosque" : "Carregando...")}
        </h1>
        <p className="mt-1 text-sm text-slate-400">Posicione o rosto no centro da câmera para registrar a chegada</p>

        {error && (
          <div className="mt-8 flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300" data-testid="kiosk-error">
            <AlertTriangle className="h-5 w-5" /> {error}
          </div>
        )}

        {!error && (
          <div className="relative mt-6 w-full max-w-xl overflow-hidden rounded-2xl border border-slate-700 bg-black shadow-2xl">
            <video ref={videoRef} autoPlay muted playsInline className="aspect-[4/3] w-full object-cover" />
            {running && <FaceGuide hint="Olhe para a câmera com o rosto dentro do contorno" />}
            {!running && (
              <div className="absolute inset-0 grid place-items-center bg-slate-900/70">
                <Button size="lg" onClick={startCamera} className="bg-blue-600 hover:bg-blue-700" data-testid="kiosk-start-button">
                  <Camera className="mr-2 h-5 w-5" /> Iniciar câmera
                </Button>
              </div>
            )}
            {busy && running && (
              <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-slate-900/80 px-3 py-1 text-xs text-slate-200">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Identificando...
              </div>
            )}
            {result && (
              <div className={`absolute inset-0 grid place-items-center p-6 text-center ${result.status === "err" ? "bg-red-900/85" : result.status === "already" ? "bg-amber-900/85" : "bg-emerald-900/90"}`} data-testid="kiosk-result">
                {result.status === "err" ? <AlertTriangle className="h-16 w-16 text-red-300" /> : result.status === "already" ? <UserCheck className="h-16 w-16 text-amber-300" /> : <CheckCircle2 className="h-16 w-16 text-emerald-300" />}
                {result.name && <div className="mt-3 font-display text-3xl font-black" data-testid="kiosk-result-name">{result.name}</div>}
                <div className="mt-1 text-lg">{result.msg}{result.time ? ` · ${result.time}` : ""}</div>
              </div>
            )}
          </div>
        )}

        {running && (
          <div className="mt-5 flex gap-3">
            <Button variant="outline" onClick={capture} disabled={busy} className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700" data-testid="kiosk-capture-button">
              <ScanFace className="mr-2 h-4 w-4" /> Registrar agora
            </Button>
            <Button variant="ghost" onClick={stopCamera} className="text-slate-400 hover:text-white" data-testid="kiosk-stop-button">Encerrar</Button>
          </div>
        )}
        {school && !error && (
          <div className="mt-6 w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900/60 p-4" data-testid="kiosk-manual">
            <div className="mb-2 text-sm font-semibold text-slate-300">Registrar por matrícula (alternativa)</div>
            <div className="flex gap-2">
              <input value={reg} onChange={(e) => setReg(e.target.value)} placeholder="Nº de matrícula"
                onKeyDown={(e) => { if (e.key === "Enter") checkin(); }}
                className="flex-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder:text-slate-500 outline-none focus:border-blue-500"
                data-testid="kiosk-reg-input" />
              <Button onClick={checkin} disabled={busy || !reg.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="kiosk-reg-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}
              </Button>
            </div>
          </div>
        )}
        {school && !school.face && !error && (
          <p className="mt-4 text-xs text-amber-400">Atenção: o reconhecimento facial ainda não está configurado para esta instância.</p>
        )}
      </div>
    </div>
  );
}
